import { useSocket } from '@mntn/sockets/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import type {
  ArchiveCampaignCallback,
  CreateCampaignCallbacks,
  DuplicateCampaignCallback,
  OnLoadCallback,
} from '@/components/shared/types'
import type {
  OrderItem,
  OrderItemsOrder,
  OrderItemsOrderBy,
  OrderItemsStatusFilter,
} from '@/flight-deck-api/services/order-items.service'
import { useFlightDeckAnalytics, useOrderItemsSummary, useOrdersWithAllocationAlerts } from '@/hooks'
import { queryClient } from '@/providers/api-client/flight-deck-api-client'
import { ActionBar } from './action-bar'
import { AllocationBanner } from './allocation-banner'
import { FilterPanel, useFilter } from './filter-panel'
import { useExpandedRows } from './hooks/use-expanded-rows'
import { useGetOrderItemsInfinite } from './hooks/use-get-order-items-infinite'
import { useTransformedOrderItemFilters } from './hooks/use-transformed-order-item-filters'
import { NoDealsFound } from './no-deals-found'
import { OrderItemDisplay } from './order-item-list'
import { SummarySection } from './summary-section'

export type SortState = {
  order: OrderItemsOrder
  orderBy: OrderItemsOrderBy
}

const DEFAULTS: {
  status: OrderItemsStatusFilter
  order: OrderItemsOrder
  orderBy: OrderItemsOrderBy
} = {
  status: 'live',
  order: 'desc',
  orderBy: 'created',
}

export type ViewMode = 'list' | 'grid'

/**
 * Whether a campaign delivered money against the deal. `hasSpendError` means reporting
 * failed, so `spend` is `null` because the number is unknown rather than zero — the campaign
 * counts as spending until reporting says otherwise.
 */
const hasSpend = (campaign: OrderItem['campaignGroups'][number]) => campaign.hasSpendError || (campaign.spend ?? 0) > 0

type DashboardBaseProps = {
  isLoading: boolean
  onDuplicateCampaign: DuplicateCampaignCallback
  onArchiveCampaign: ArchiveCampaignCallback
  onLoad?: OnLoadCallback
}

export type DashboardProps = DashboardBaseProps & CreateCampaignCallbacks

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const Dashboard: React.FC<DashboardProps> = props => {
  const { isLoading, onCreateCampaignV2, onDuplicateCampaign, onArchiveCampaign, onLoad } = props
  const [status, setStatus] = useState<OrderItemsStatusFilter>(DEFAULTS.status)
  const [search, setSearch] = useState('')
  const [sortState, setSortState] = useState<SortState>({
    order: DEFAULTS.order,
    orderBy: DEFAULTS.orderBy,
  })
  const [view, setView] = useState<ViewMode>('grid')
  const { trackDashboardViewed } = useFlightDeckAnalytics()

  useSocket({
    onMessage: async ({ topic }) => {
      switch (topic) {
        case 'order-updated':
          await Promise.all([
            // Invalidate all order items queries
            queryClient.invalidateQueries({
              queryKey: ['orderItems'],
            }),
            // Also invalidate the summary queries that show campaign counts
            queryClient.invalidateQueries({
              queryKey: ['order-items-aggregated-summary'],
            }),
            // Invalidate status summary queries
            queryClient.invalidateQueries({
              queryKey: ['orderItemsStatusSummary'],
            }),
            // Invalidate impression assignment form cache to show updated available impressions
            queryClient.invalidateQueries({
              queryKey: ['getOrderItemByCampaignGroupId'],
            }),
          ])
          break
      }
    },
  })

  // Get filter state and transform for API
  const { debouncedFilters } = useFilter()
  const transformedFilters = useTransformedOrderItemFilters(debouncedFilters)

  // Fetch summary data
  const { statusSummary, orderItemSummary } = useOrderItemsSummary({
    status,
    search,
    filters: transformedFilters,
  })

  const hasTrackedView = useRef(false)

  // Track dashboard viewed event when data is ready
  useEffect(() => {
    if (hasTrackedView.current || !statusSummary || !orderItemSummary) return

    const totalOrderItems = statusSummary.upcoming + statusSummary.live + statusSummary.completed

    const dealsAvailableForCampaigns = orderItemSummary.upcomingDeals.total - orderItemSummary.upcomingDeals.effective

    const metadata = {
      has_purchased_deals: totalOrderItems > 0,
      ready_to_create_campaign: dealsAvailableForCampaigns > 0,
    }

    trackDashboardViewed(metadata)

    onLoad?.(metadata)

    hasTrackedView.current = true
  }, [statusSummary, orderItemSummary, trackDashboardViewed, onLoad])

  const resetOnDeps = useMemo(() => [status, search, sortState], [status, search, sortState])

  const { expandedRows, toggleRow } = useExpandedRows({
    resetOn: resetOnDeps,
  })

  const {
    orderItems,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    page,
    status: fetchStatus,
  } = useGetOrderItemsInfinite({
    status,
    search,
    order: sortState.order,
    orderBy: sortState.orderBy,
    // Pass the transformed filters to the API call
    ...transformedFilters,
  })

  const [sentryRef] = useInfiniteScroll({
    loading: Boolean(isFetching || isFetchingNextPage),
    hasNextPage,
    onLoadMore: fetchNextPage,
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: fetchStatus === 'error',
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 200px 0px',
  })

  const hasNoResults = !isFetching && orderItems.length === 0
  const alertOrders = useOrdersWithAllocationAlerts(orderItems)

  useEffect(() => {
    if (search) {
      setSortState({ order: 'desc', orderBy: 'search' })
    } else {
      setSortState({ order: DEFAULTS.order, orderBy: DEFAULTS.orderBy })
    }
  }, [search])

  // Log filter changes for debugging
  useEffect(() => {
    if (Object.keys(debouncedFilters).length > 0) {
      // Filter changes applied
    }
  }, [debouncedFilters])

  // Wrap onDuplicateCampaign to invalidate cache after Premier confirms campaign duplication
  const handleDuplicateCampaign = useCallback(
    async (campaignId: number, isExperiment?: boolean) => {
      try {
        // Wait for Premier to show campaign naming modal and duplicate the campaign
        const result = await onDuplicateCampaign(campaignId, isExperiment)

        return result
      } catch (error) {
        console.error('Flight Deck: Campaign duplication failed:', error)
        // Don't invalidate cache if duplication failed
        throw error
      }
    },
    [onDuplicateCampaign],
  )

  const handleArchiveCampaign = useCallback(
    async (campaignId: number) => {
      try {
        const result = await onArchiveCampaign(campaignId)

        if (!result?.refId) return undefined

        // Optimistically update the cache to what the API will report on the next fetch:
        // an archived campaign stays listed, as ARCHIVED, only if it spent.
        queryClient.setQueriesData<{
          pages: Array<{
            data: Array<OrderItem>
          }>
        }>({ queryKey: ['orderItems'] }, oldData => {
          if (!oldData?.pages) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              data: page.data.map(item =>
                item.orderItemId === result.refId
                  ? {
                      ...item,
                      campaignGroups: item.campaignGroups.flatMap(c => {
                        if (c.campaignGroupId !== campaignId) return [c]
                        if (!hasSpend(c)) return []
                        return [{ ...c, status: 'ARCHIVED' }]
                      }),
                    }
                  : item,
              ),
            })),
          }
        })

        return result
      } catch (error) {
        console.error('Flight Deck: Campaign archive failed:', error)
        throw error
      }
    },
    [onArchiveCampaign],
  )

  return (
    <div className="flex relative w-full h-full bg-background-inverse">
      <div className="flex flex-col flex-1 w-full">
        <div className="flex flex-col min-h-full text-header-inverse px-4">
          <div className="flex flex-col items-center">
            <ActionBar
              status={status}
              setStatus={setStatus}
              search={search}
              setSearch={setSearch}
              sortState={sortState}
              setSortState={setSortState}
              view={view}
              setView={setView}
            />
          </div>
          <SummarySection status={status} search={search} filters={transformedFilters} />
          <AllocationBanner
            alertOrders={alertOrders}
            onCreateCampaignV2={onCreateCampaignV2}
            onDuplicateCampaign={handleDuplicateCampaign}
            onArchiveCampaign={handleArchiveCampaign}
            isLoading={isLoading}
          />
          <div className="flex flex-col gap-1 mb-2 flex-1 items-center">
            {hasNoResults ? (
              <NoDealsFound />
            ) : (
              <OrderItemDisplay
                orderItems={orderItems}
                isLoading={isLoading}
                onCreateCampaignV2={onCreateCampaignV2}
                onDuplicateCampaign={handleDuplicateCampaign}
                onArchiveCampaign={handleArchiveCampaign}
                isFetching={isFetching}
                currentPage={page.currentPage}
                pageSize={page.limit}
                lastItemRef={sentryRef}
                expandedRows={expandedRows}
                toggleRow={toggleRow}
                view={view}
              />
            )}
          </div>
        </div>
      </div>
      <FilterPanel />
    </div>
  )
}
